import { Waves, Wind, Thermometer, Compass, Timer, CloudSun, Activity, Droplets, ArrowUp } from "lucide-react";
import type { Conditions } from "@/lib/surf";
import { degToCompass, weatherLabel, conditionRating } from "@/lib/surf";
import type { Surfbreak } from "@/data/surfbreaks";

interface Props { c: Conditions; b: Surfbreak; }

const Stat = ({ icon: Icon, label, value, sub, arrowDeg }: { icon: any; label: string; value: string; sub?: string; arrowDeg?: number }) => (
  <div className="py-4 border-b border-border">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2">
      <Icon className="h-3 w-3" /> {label}
    </div>
    <div className="flex items-center gap-3">
      <div className="text-2xl font-bold text-foreground leading-none">{value}</div>
      {arrowDeg !== undefined && (
        <ArrowUp
          className="h-6 w-6 text-primary shrink-0 transition-transform"
          style={{ transform: `rotate(${arrowDeg}deg)` }}
          aria-label={`Direction ${Math.round(arrowDeg)}°`}
        />
      )}
    </div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

export const ConditionsPanel = ({ c, b }: Props) => {
  const rating = conditionRating(c);
  const ft = (m: number) => (m * 3.28084).toFixed(1);

  return (
    <div className="px-4 py-6">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Live Report</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl text-foreground mt-1">{b.name}</h2>
          <p className="text-sm text-muted-foreground">{b.region}, {b.country} · {b.type} break</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-sunset text-accent-foreground text-xs font-bold uppercase tracking-widest">
          <Activity className="h-3.5 w-3.5" /> {rating.label}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-0">
        <Stat icon={Waves} label="Wave height" value={`${c.waveHeight.toFixed(1)} m`} sub={`${ft(c.waveHeight)} ft`} />
        <Stat icon={Timer} label="Wave period" value={`${c.wavePeriod.toFixed(1)} s`} />
        <Stat icon={Compass} label="Swell" value={`${c.swellHeight.toFixed(1)} m`} sub={`${c.swellPeriod.toFixed(1)}s · ${degToCompass(c.waveDirection)}`} arrowDeg={c.waveDirection} />
        <Stat icon={Wind} label="Wind" value={`${c.windSpeed.toFixed(0)} km/h`} sub={`${degToCompass(c.windDirection)} · gusts ${c.windGusts.toFixed(0)}`} arrowDeg={c.windDirection} />
        <Stat icon={Thermometer} label="Air temp" value={`${c.airTemp.toFixed(0)}°C`} />
        <Stat icon={Droplets} label="Sea temp" value={`${(c.seaTemp ?? 0).toFixed(1)}°C`} sub={`${((c.seaTemp ?? 0) * 9/5 + 32).toFixed(0)}°F`} />
        <Stat icon={CloudSun} label="Weather" value={weatherLabel(c.weatherCode)} />
        <Stat icon={Compass} label="Swell dir" value={`${Math.round(c.waveDirection)}°`} sub={degToCompass(c.waveDirection)} arrowDeg={c.waveDirection} />
      </div>
    </div>
  );
};
